import React, { useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import Button from "../../../components/button/Button";
import { useInitiateUploadMutation } from "../../../app/services/uploadApi";
import {
  useCreateNoteChangeMutation,
  useCreateNoteMutation,
  type NoteSection,
} from "../../../app/services/notes";
import { importNoteEntries, isMarkdownFileName } from "../utils/importNotes";

/**
 * Markdown / 画像ファイルのインポートモーダル。
 *
 * 取り込みの実体は importNoteEntries（サイドバーへの OS ファイル D&D と共通）:
 * - .md / .markdown / .txt はファイル名（拡張子除く）をタイトルとしてノート化
 * - 画像ファイルは purpose=note で S3 にアップロードし、md 内の相対パス参照
 *   （`![alt](images/foo.png)` / Obsidian の `![[foo.png]]`）をアップロード後の
 *   URL に置換する
 * - 直接更新権限が無いセクション（承認者が複数いる等）では、ノートを
 *   直接作成せず下書き（変更提案）として取り込み、後からプルリクエストで
 *   提出できるようにする
 */

const DropArea = styled.div<{ $dragging: boolean }>`
  border: 2px dashed
    ${({ $dragging }) =>
      $dragging ? "var(--soremo-blue)" : "var(--soremo-border)"};
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  background: ${({ $dragging }) => ($dragging ? "#f0faff" : "#fcfcfc")};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .material-symbols-rounded {
    font-size: 18px;
    color: var(--soremo-light-gray);
  }

  .file-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface ImportNotesModalProps {
  section: NoteSection;
  onClose: () => void;
}

const ImportNotesModal: React.FC<ImportNotesModalProps> = ({
  section,
  onClose,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [initiateUpload] = useInitiateUploadMutation();
  const [createNote] = useCreateNoteMutation();
  const [createNoteChange] = useCreateNoteChangeMutation();

  // 直接更新できないセクションでは下書き（変更提案）として取り込む
  const importsAsDraft = !section.can_direct_update;

  const mdFiles = files.filter((f) => isMarkdownFileName(f.name));

  const addFiles = (added: File[]) => {
    const accepted = added.filter(
      (f) => isMarkdownFileName(f.name) || f.type.startsWith("image/"),
    );
    if (accepted.length !== added.length) {
      toast.error("Markdown（.md / .txt）と画像ファイルのみインポートできます");
    }
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...accepted.filter((f) => !names.has(f.name))];
    });
  };

  const handleImport = async () => {
    if (mdFiles.length === 0) {
      toast.error("Markdown ファイルを選択してください");
      return;
    }
    setImporting(true);
    try {
      // ファイル選択はフラット（フォルダ階層なし）なので relativePath = ファイル名
      const { noteCount } = await importNoteEntries(
        files.map((file) => ({ file, relativePath: file.name })),
        {
          sectionId: section.id,
          initiateUpload,
          createNote: importsAsDraft
            ? (params) =>
                createNoteChange({
                  target_type: "note",
                  section_id: params.section_id,
                  payload: {
                    title: params.title,
                    content: params.content ?? "",
                  },
                }).unwrap()
            : (params) => createNote(params).unwrap(),
        },
      );
      toast.success(
        importsAsDraft
          ? `${noteCount}件を下書きとして取り込みました。「プルリクエスト」から提出できます`
          : `${noteCount}件のノートをインポートしました`,
      );
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "インポートに失敗しました",
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      title="ノートをインポート"
      titleSize="medium"
      size="default"
      description={
        importsAsDraft
          ? `「${section.title}」に Markdown ファイルを下書きとして取り込みます（一覧の「下書き」行から編集し、プルリクエストで提出できます）`
          : `「${section.title}」に Markdown ファイルをノートとして取り込みます`
      }
      onClose={onClose}
      primaryAction={{
        label: importing
          ? "インポート中..."
          : importsAsDraft
            ? `下書きとして取り込む（${mdFiles.length}件）`
            : `インポート（${mdFiles.length}件）`,
        onClick: handleImport,
        disabled: mdFiles.length === 0,
        loading: importing,
      }}
      secondaryAction={{ label: "キャンセル", onClick: onClose }}
    >
      <div className="u-col u-gap16">
        <DropArea
          $dragging={dragging}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(Array.from(e.dataTransfer.files));
          }}
        >
          <p className="bodytext-13 u-text-gray u-line-height-150">
            .md / .txt ファイルと画像をここにドロップ
            <br />
            またはクリックして選択
          </p>
          <p className="bodytext-11 u-text-light-gray u-line-height-150">
            md 内の相対パス画像（`![](images/foo.png)` / `![[foo.png]]`）は
            同時に選択した画像で自動置換されます
          </p>
        </DropArea>
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,.txt,image/*"
          multiple
          hidden
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />

        {files.length > 0 && (
          <div className="u-col u-gap4">
            {files.map((file) => (
              <FileRow key={file.name}>
                <span className="material-symbols-rounded">
                  {isMarkdownFileName(file.name) ? "description" : "image"}
                </span>
                <span className="file-name bodytext-13">{file.name}</span>
                <Button
                  size="small"
                  theme="tertiary"
                  variant="text"
                  onClick={() =>
                    setFiles((prev) => prev.filter((f) => f !== file))
                  }
                >
                  削除
                </Button>
              </FileRow>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportNotesModal;
